import useAppState from "@dashboard/hooks/useAppState";
import { glassBackdropStyle, glassNavStyle, glassPaneStyle } from "@dashboard/theme/flexgPalette";
import { LinearProgress } from "@material-ui/core";
import { Box } from "@saleor/macaw-ui-next";
import type * as React from "react";

import { DevModePanel } from "../DevModePanel/DevModePanel";
import NavigatorSearch from "../NavigatorSearch";
import { useSavebarRef } from "../Savebar/SavebarRefContext";
import { Sidebar } from "../Sidebar";
import { MenuStructureProvider } from "../Sidebar/menu/MenuStructureContext";
import { SidebarProvider } from "../Sidebar/SidebarContext";
import { isDetailPopupWindow, savebarHeight, topMenuBarHeight } from "./consts";
import { useStyles } from "./styles";
import { TopMenuBar } from "./TopMenuBar";

interface AppLayoutProps {
  children: React.ReactNode;
  fullSize?: boolean;
}

const AppLayout = ({ children }: AppLayoutProps) => {
  const classes = useStyles();
  const { isSavebarMounted, setAnchor } = useSavebarRef();
  const [appState] = useAppState();
  // 목록에서 연 상세 팝업 창은 메뉴·상단바 없이 내용만 (2026-10-02 KST 추가)
  const isPopup = isDetailPopupWindow();

  return (
    <SidebarProvider>
      <MenuStructureProvider>
        <DevModePanel />
        <NavigatorSearch />

        <Box
          display="grid"
          __gridTemplateColumns={isPopup ? "1fr" : "auto 1fr"}
          // 둘째 행은 auto — 격자 높이가 콘텐츠로 정해지던 종전 동작을 그대로 둔다.
          // 사이드바가 두 행에 걸친 100vh 라, 콘텐츠가 짧아도 행 합이 100vh 밑으로는 안 내려간다.
          __gridTemplateRows={`${topMenuBarHeight} auto`}
          style={
            isPopup ? ({ "--app-top-menu-bar-height": "0px" } as React.CSSProperties) : undefined
          }
        >
          {appState.loading && <LinearProgress className={classes.appLoader} color="primary" />}
          {!isPopup && (
            <>
              <Box
                __gridColumn="1"
                __gridRow="1 / -1"
                height="100vh"
                // 사이드바 안쪽 컴포넌트가 읽는 macaw 변수를 이 subtree 에서만 갈아끼워
                // 어둡게 만든다. (2026-09-17 KST 추가)
                //
                // 원본에 있던 오른쪽 1px 구분선은 뺐다. 밝은 사이드바가 흰 콘텐츠와 맞닿던 시절의
                // 것이라, 지금처럼 사이드바와 상단 메뉴바가 같은 어두운 색이면 둘 사이에 밝은
                // 이음매만 남는다. 흰 콘텐츠와는 색 차이만으로 경계가 선다. (2026-09-21 KST 수정)
                //
                // 리퀴드 글래스 시험 중이라 두 겹이다. 바깥이 흐려질 바탕, 안쪽이 유리판.
                // 되돌리려면 안쪽 Box 를 걷어내고 바깥에 style={darkNavStyle}
                // backgroundColor="default2" 를 주면 된다. (2026-09-21 KST 수정)
                style={glassBackdropStyle}
                position="sticky"
                top={0}
              >
                <Box height="100%" style={{ ...glassNavStyle, ...glassPaneStyle }}>
                  <Sidebar />
                </Box>
              </Box>
              {/*
            상단 메뉴바. 사이드바 오른쪽 열의 첫 행에만 보이지만, 격자 영역은 일부러 두 행에
            걸쳐 두고 alignSelf="start" 로 첫 행에만 그린다.
            sticky 가 움직일 수 있는 범위는 그 요소의 격자 영역까지다. 첫 행(50px)만
            차지하게 두면 50px 만 스크롤해도 바가 위로 딸려 올라가 버린다.
            콘텐츠 열은 overflow:hidden 이라 스크롤 컨테이너가 되므로 바를 그 안에 두어도
            창 스크롤에는 붙지 않는다. 그래서 격자 직계로 꺼냈다. (2026-09-17 KST 수정)
          */}
              <Box
                __gridColumn="2"
                __gridRow="1 / -1"
                alignSelf="start"
                position="sticky"
                top={0}
                zIndex="3"
                // 상단바 유리가 흐릴 바탕. 사이드바와 같은 것을 깔아야 둘이 한 장으로 보인다.
                // 스크롤하면 흰 콘텐츠가 이 바 밑으로 지나가는데, 그게 그대로 비치면 바 색이
                // 오락가락해 메뉴 글자가 읽기 어려워진다. 이 바탕이 그걸 가린다.
                // (2026-09-21 KST 추가)
                style={glassBackdropStyle}
              >
                <TopMenuBar />
              </Box>
            </>
          )}

          <Box
            __gridColumn={isPopup ? "1" : "2"}
            __gridRow="2"
            height="100%"
            width="100%"
            overflow="hidden"
          >
            <Box as="main" width="100%" height="100%">
              {children}
            </Box>
            <Box
              ref={setAnchor}
              position="sticky"
              bottom={0}
              left={0}
              right={0}
              // 배경은 안쪽 Savebar 가 유리로 칠한다. 여기서 흰색으로 채우면 그 유리
              // 뒤가 불투명해져 아무것도 비치지 않는다. (2026-09-21 KST 수정)
              borderTopWidth={isSavebarMounted ? 1 : 0}
              borderTopStyle="solid"
              borderColor="default1"
              zIndex="2"
              __height={isSavebarMounted ? savebarHeight : "0"}
              overflow="hidden"
            />
          </Box>
        </Box>
      </MenuStructureProvider>
    </SidebarProvider>
  );
};

export default AppLayout;
